/**
 * FO-307-A1 - dunne, promise-gebaseerde IndexedDB-wrapper (geen dependency).
 *
 * Twee stores: `mutations` (de offline-wachtrij) en `reads` (read-cache vandaag
 * ±1). Bewust zonder de `idb`-library om de lockfile en bundle klein te houden;
 * de API die we nodig hebben is minimaal.
 *
 * Alle functies falen zacht wanneer IndexedDB niet beschikbaar is (SSR of een
 * private-mode-browser): ze rejecten met een duidelijke fout die de aanroeper
 * afvangt (de app blijft online gewoon werken).
 */
export const DB_NAME = 'streep-offline'
export const DB_VERSION = 1
export const STORE_MUTATIONS = 'mutations'
export const STORE_READS = 'reads'

let dbPromise: Promise<IDBDatabase> | null = null

function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined'
}

function openDb(): Promise<IDBDatabase> {
  if (!hasIndexedDb()) return Promise.reject(new Error('IndexedDB niet beschikbaar'))
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const database = req.result
      if (!database.objectStoreNames.contains(STORE_MUTATIONS)) {
        const store = database.createObjectStore(STORE_MUTATIONS, { keyPath: 'id' })
        store.createIndex('byStatus', 'status')
        store.createIndex('byCreatedAt', 'createdAt')
      }
      if (!database.objectStoreNames.contains(STORE_READS)) {
        database.createObjectStore(STORE_READS, { keyPath: 'key' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open faalde'))
  })
  return dbPromise
}

function runRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB-verzoek faalde'))
  })
}

export async function idbGetAll<T>(store: string): Promise<T[]> {
  const db = await openDb()
  const tx = db.transaction(store, 'readonly')
  return runRequest<T[]>(tx.objectStore(store).getAll() as IDBRequest<T[]>)
}

export async function idbGet<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openDb()
  const tx = db.transaction(store, 'readonly')
  return runRequest<T | undefined>(tx.objectStore(store).get(key) as IDBRequest<T | undefined>)
}

export async function idbPut<T>(store: string, value: T): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(store, 'readwrite')
  await runRequest(tx.objectStore(store).put(value))
}

export async function idbDelete(store: string, key: IDBValidKey): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(store, 'readwrite')
  await runRequest(tx.objectStore(store).delete(key))
}

/** Voor tests / reset - sluit de gecachete verbinding. */
export function resetDbConnection(): void {
  dbPromise = null
}
